import { getStorage } from './storageAdapter';

const normalize = (value) => String(value ?? '').trim();
const normalizeLower = (value) => normalize(value).toLowerCase();
const escapeLike = (value) => normalizeLower(value).replace(/[\\%_]/g, '\\$&');
const asFlag = (value) => (value === true || value === 1 || value === '1' ? 1 : 0);
const MOBILE_EXECUTE_SET_BATCH_SIZE = 8;

const UPSERT_STOCK_SQL = `
INSERT INTO stock_items (
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  updated_at,
  data_json
)
VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
ON CONFLICT(plu) DO UPDATE SET
  barcode = excluded.barcode,
  description = excluded.description,
  description2 = excluded.description2,
  department = excluded.department,
  sub_dep = excluded.sub_dep,
  sub_dep2 = excluded.sub_dep2,
  fast_line = excluded.fast_line,
  archived = excluded.archived,
  has_image = excluded.has_image,
  updated_at = excluded.updated_at,
  data_json = excluded.data_json
`;

class StockRepository {
    constructor() {
        this.schemaReady = false;
        this.schemaPromise = null;
        this.replaceStockQueue = Promise.resolve();
    }

    async ensureSchema() {
        if (this.schemaReady) return true;
        if (this.schemaPromise) return this.schemaPromise;

        this.schemaPromise = (async () => {
            try {
                const storage = await getStorage();
                if (!storage || storage.platform === 'indexeddb') {
                    return false;
                }

                await storage.run(`
CREATE TABLE IF NOT EXISTS stock_items (
  plu TEXT PRIMARY KEY,
  barcode TEXT,
  description TEXT NOT NULL,
  description2 TEXT,
  department TEXT,
  sub_dep TEXT,
  sub_dep2 TEXT,
  fast_line INTEGER DEFAULT 0,
  archived INTEGER DEFAULT 0,
  has_image INTEGER DEFAULT 0,
  updated_at TEXT NOT NULL,
  data_json TEXT NOT NULL
)`);

                await storage.run(`CREATE INDEX IF NOT EXISTS idx_stock_barcode ON stock_items(barcode)`);
                await storage.run(`CREATE INDEX IF NOT EXISTS idx_stock_department ON stock_items(department, sub_dep, sub_dep2)`);
                await storage.run(`CREATE INDEX IF NOT EXISTS idx_stock_updated_at ON stock_items(updated_at)`);
                await storage.run(`CREATE INDEX IF NOT EXISTS idx_stock_fast_line ON stock_items(fast_line, archived)`);

                this.schemaReady = true;
                return true;
            } catch (error) {
                console.warn('[StockRepository] Failed to ensure stock schema:', error);
                return false;
            } finally {
                this.schemaPromise = null;
            }
        })();

        return this.schemaPromise;
    }

    toDbRow(item) {
        const plu = normalize(item?.PLU);
        if (!plu) return null;

        let dataJson = '{}';
        try {
            dataJson = JSON.stringify(item ?? {});
        } catch (_) {
            dataJson = '{}';
        }

        return {
            plu,
            barcode: normalize(item?.barcode ?? item?.Barcode),
            description: normalize(item?.Description),
            description2: normalize(item?.Description2),
            department: normalize(item?.Department),
            subDep: normalize(item?.SubDep),
            subDep2: normalize(item?.Subdep2),
            fastLine: asFlag(item?.FastLine),
            archived: asFlag(item?.archived),
            hasImage: asFlag(item?.hasImage || item?.Image),
            updatedAt: normalize(item?.UpdatedAt || item?.UpdatedDate || new Date().toISOString()),
            dataJson
        };
    }

    fromDbRow(row) {
        if (!row) return null;
        try {
            const parsed = JSON.parse(row.data_json);
            if (parsed && typeof parsed === 'object') return parsed;
        } catch (_) {
            // Ignore parse failures and fall through to minimal shape.
        }

        return {
            PLU: row.plu,
            barcode: row.barcode || '',
            Description: row.description || '',
            Description2: row.description2 || '',
            Department: row.department || '',
            SubDep: row.sub_dep || '',
            Subdep2: row.sub_dep2 || '',
            FastLine: Number(row.fast_line || 0),
            archived: Number(row.archived || 0) === 1,
            hasImage: Number(row.has_image || 0) === 1
        };
    }

    async replaceStock(items = []) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 500;

        const runReplace = async () => {
            const ready = await this.ensureSchema();
            if (!ready) return false;

            try {
                const storage = await getStorage();
                const sqlItems = Array.isArray(items) ? items : [];
                const useTransaction = storage.platform !== 'indexeddb';
                const canUseMobileBatch = storage?.platform === 'mobile'
                    && storage?.dbConnection
                    && typeof storage.dbConnection.executeSet === 'function';

                // Try to start a transaction, but fall back to non-transactional mode
                // if the DB is busy (e.g. table server sync holds a transaction).
                // Stock replacement is idempotent — worst case it re-syncs next cycle.
                let inTransaction = false;
                if (useTransaction) {
                    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                        try {
                            await storage.run('BEGIN IMMEDIATE');
                            inTransaction = true;
                            break;
                        } catch (beginErr) {
                            if (String(beginErr?.message || beginErr).includes('transaction') && attempt < MAX_RETRIES - 1) {
                                console.warn(`[StockRepository] Transaction busy, retrying in ${RETRY_DELAY_MS}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
                                await new Promise(r => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
                            } else {
                                console.warn('[StockRepository] Could not acquire transaction, proceeding without one');
                                break;
                            }
                        }
                    }
                }

                try {
                    await storage.run('DELETE FROM stock_items');

                    if (canUseMobileBatch) {
                        let batchValues = [];

                        const flushBatch = async () => {
                            if (batchValues.length === 0) return;
                            try {
                                await storage.dbConnection.executeSet([{
                                    statement: UPSERT_STOCK_SQL,
                                    values: batchValues
                                }], false, 'no', true);
                            } catch (batchError) {
                                // Fallback for driver-specific edge cases in batched statements.
                                for (let i = 0; i < batchValues.length; i++) {
                                    await storage.run(UPSERT_STOCK_SQL, batchValues[i]);
                                }
                            }
                            batchValues = [];
                        };

                        for (let i = 0; i < sqlItems.length; i++) {
                            const row = this.toDbRow(sqlItems[i]);
                            if (!row) continue;

                            batchValues.push([
                                row.plu,
                                row.barcode,
                                row.description,
                                row.description2,
                                row.department,
                                row.subDep,
                                row.subDep2,
                                row.fastLine,
                                row.archived,
                                row.hasImage,
                                row.updatedAt,
                                row.dataJson
                            ]);

                            if (batchValues.length >= MOBILE_EXECUTE_SET_BATCH_SIZE) {
                                await flushBatch();
                            }
                        }

                        await flushBatch();
                    } else {
                        for (let i = 0; i < sqlItems.length; i++) {
                            const row = this.toDbRow(sqlItems[i]);
                            if (!row) continue;

                            await storage.run(UPSERT_STOCK_SQL, [
                                row.plu,
                                row.barcode,
                                row.description,
                                row.description2,
                                row.department,
                                row.subDep,
                                row.subDep2,
                                row.fastLine,
                                row.archived,
                                row.hasImage,
                                row.updatedAt,
                                row.dataJson
                            ]);
                        }
                    }

                    if (inTransaction) {
                        await storage.run('COMMIT');
                    }
                } catch (error) {
                    if (inTransaction) {
                        try { await storage.run('ROLLBACK'); } catch (_) {}
                    }
                    throw error;
                }

                return true;
            } catch (error) {
                console.warn('[StockRepository] Failed to replace stock:', error);
                return false;
            }
        };

        // Renderer calls can overlap (Vuex commits are sync), so serialize full-replace writes.
        const queuedRun = this.replaceStockQueue.then(runReplace, runReplace);
        this.replaceStockQueue = queuedRun.catch(() => {});
        return queuedRun;
    }

    async upsertStockItem(item) {
        const ready = await this.ensureSchema();
        if (!ready) return false;

        const row = this.toDbRow(item);
        if (!row) return false;

        try {
            const storage = await getStorage();
            await storage.run(UPSERT_STOCK_SQL, [
                row.plu,
                row.barcode,
                row.description,
                row.description2,
                row.department,
                row.subDep,
                row.subDep2,
                row.fastLine,
                row.archived,
                row.hasImage,
                row.updatedAt,
                row.dataJson
            ]);
            return true;
        } catch (error) {
            console.warn('[StockRepository] Failed to upsert stock item:', error);
            return false;
        }
    }

    async getAllStock() {
        const ready = await this.ensureSchema();
        if (!ready) return null;

        try {
            const storage = await getStorage();

            // Paginate reads to avoid OOM on the Capacitor SQLite native bridge.
            // The bridge serializes query results to a single JSON string; 80k+ rows
            // with data_json blobs can exceed 150MB and crash Android.
            const PAGE_SIZE = 2000;
            const allItems = [];
            let offset = 0;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const rows = await storage.query(`
SELECT
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  data_json
FROM stock_items
ORDER BY description ASC
LIMIT ? OFFSET ?
`, [PAGE_SIZE, offset]);

                if (!rows || rows.length === 0) break;

                for (let i = 0; i < rows.length; i++) {
                    const item = this.fromDbRow(rows[i]);
                    if (item) allItems.push(item);
                }

                if (rows.length < PAGE_SIZE) break;
                offset += PAGE_SIZE;
            }

            return allItems;
        } catch (error) {
            console.warn('[StockRepository] Failed to read all stock:', error);
            return null;
        }
    }

    async getByPlu(plu) {
        const ready = await this.ensureSchema();
        if (!ready) return null;

        const pluKey = normalizeLower(plu);
        if (!pluKey) return null;

        try {
            const storage = await getStorage();
            const row = await storage.get(`
SELECT
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  data_json
FROM stock_items
WHERE lower(plu) = ?
LIMIT 1
`, [pluKey]);

            return this.fromDbRow(row);
        } catch (error) {
            console.warn('[StockRepository] Failed to get stock by PLU:', error);
            return null;
        }
    }

    async getFastLine() {
        const ready = await this.ensureSchema();
        if (!ready) return null;

        try {
            const storage = await getStorage();
            const rows = await storage.query(`
SELECT
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  data_json
FROM stock_items
WHERE fast_line = 1
ORDER BY description ASC
`);

            return (rows || []).map((row) => this.fromDbRow(row)).filter(Boolean);
        } catch (error) {
            console.warn('[StockRepository] Failed to get fast line stock:', error);
            return null;
        }
    }

    buildCategoryFilter(whereParts, params, query = {}) {
        const category = normalizeLower(query.category);
        const subCategory = normalizeLower(query.subCategory);
        const sub2Category = normalizeLower(query.sub2Category);

        if (category) {
            whereParts.push('lower(department) = ?');
            params.push(category);
        }
        if (subCategory) {
            whereParts.push('lower(sub_dep) = ?');
            params.push(subCategory);
        }
        if (sub2Category) {
            whereParts.push('lower(sub_dep2) = ?');
            params.push(sub2Category);
        }
    }

    buildSearchTermFilter(whereParts, params, description = '') {
        const term = escapeLike(description);
        if (!term) return;

        const likeTerm = `%${term}%`;
        whereParts.push(`
(
  lower(description) LIKE ? ESCAPE '\\'
  OR lower(description2) LIKE ? ESCAPE '\\'
  OR lower(plu) LIKE ? ESCAPE '\\'
  OR lower(barcode) LIKE ? ESCAPE '\\'
)`);
        params.push(likeTerm, likeTerm, likeTerm, likeTerm);
    }

    async searchStockDesc(query = {}, limit = 5) {
        const ready = await this.ensureSchema();
        if (!ready) return null;

        try {
            const storage = await getStorage();
            const safeLimit = Number.isFinite(Number(limit)) ? Math.max(1, Number(limit)) : 5;

            const whereParts = ['archived = 0'];
            const params = [];
            this.buildCategoryFilter(whereParts, params, query);
            this.buildSearchTermFilter(whereParts, params, query.description);

            const whereSql = whereParts.join(' AND ');

            const rows = await storage.query(`
SELECT
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  data_json
FROM stock_items
WHERE ${whereSql}
ORDER BY description ASC
LIMIT ?
`, [...params, safeLimit]);

            return (rows || []).map((row) => this.fromDbRow(row)).filter(Boolean);
        } catch (error) {
            console.warn('[StockRepository] Failed to search stock by description:', error);
            return null;
        }
    }

    async searchStock(query = {}, limit = 50, offset = 0) {
        const ready = await this.ensureSchema();
        if (!ready) return null;

        const category = normalize(query.category);
        if (!category) {
            return { rows: [], total: 0 };
        }

        try {
            const storage = await getStorage();
            const safeLimit = Number.isFinite(Number(limit)) ? Math.max(1, Number(limit)) : 50;
            const safeOffset = Number.isFinite(Number(offset)) ? Math.max(0, Number(offset)) : 0;

            const whereParts = [];
            const params = [];
            this.buildCategoryFilter(whereParts, params, query);
            this.buildSearchTermFilter(whereParts, params, query.description);

            const whereSql = whereParts.length ? whereParts.join(' AND ') : '1=1';

            const countRow = await storage.get(`
SELECT COUNT(1) AS total
FROM stock_items
WHERE ${whereSql}
`, params);

            const rows = await storage.query(`
SELECT
  plu,
  barcode,
  description,
  description2,
  department,
  sub_dep,
  sub_dep2,
  fast_line,
  archived,
  has_image,
  data_json
FROM stock_items
WHERE ${whereSql}
ORDER BY description ASC
LIMIT ? OFFSET ?
`, [...params, safeLimit, safeOffset]);

            return {
                total: Number(countRow?.total || 0),
                rows: (rows || []).map((row) => this.fromDbRow(row)).filter(Boolean)
            };
        } catch (error) {
            console.warn('[StockRepository] Failed to search stock:', error);
            return null;
        }
    }

    /**
     * Wipe the entire local catalogue.
     * Used on sign-out and StoreDB change so the next login cannot
     * hydrate a previous store's stock (stock_items is not keyed by StoreDB).
     */
    async clearAllStock() {
        return this.replaceStock([]);
    }
}

const stockRepository = new StockRepository();
export default stockRepository;
